import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import { Link } from '@/components/ui/link';

import { ListTasks } from '../api/queries/list-tasks';
import type { TaskPreview } from '../entities/task-preview';

import {
  type TaskCardCreateForm,
  taskCardCreateForm,
} from './task-card-create';

export type DuplicateTitleDialogProps = {
  firstTitle: string;
  onCreate: (data: TaskCardCreateForm) => Promise<void>;
} & Pick<DialogProps, 'open' | 'onClose'>;

export function DuplicateTitleDialog({
  firstTitle,
  onCreate,
  ...props
}: DuplicateTitleDialogProps) {
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const form = useForm<TaskCardCreateForm>({
    resolver: zodResolver(taskCardCreateForm),
  });
  const { onClose } = props;

  const data = ListTasks.useQuery({});
  const task = data.data?.tasks?.find((t) => t.title === firstTitle);
  const path = task?.id
    ? generatePath('/tasks?id=:taskId', { taskId: task.id })
    : null;

  async function handleSubmit(data: TaskCardCreateForm) {
    try {
      await toast.promise(onCreate(data), {
        success: 'Task created',
      });
      props.onClose(false);
    } catch (_) {
      form.setFocus('title');
      form.setError('title', { type: 'manual', message: 'Name already taken' });
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      onClose(false);
      event.stopPropagation();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitRef.current?.click();
      event.stopPropagation();
    }
  }

  useEffect(() => {
    if (data.data?.tasks) {
      const title = getUniqueTitle(data.data.tasks, firstTitle);
      form.reset({ title });
    }
  }, [form, data.data?.tasks, firstTitle]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Dialog {...props} className="mt-[33%]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogTitle>Card already exists</DialogTitle>
          <DialogDescription>
            The task{' '}
            {path ? (
              <Link
                className="cursor-pointer text-primary-600 hover:text-primary-500"
                to={path}
              >
                "{firstTitle}"
              </Link>
            ) : (
              firstTitle
            )}{' '}
            already exists. Please choose a different title.
          </DialogDescription>
          <DialogBody>
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      ref={(e) => {
                        field.ref(e);
                        inputRef.current = e;
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button
              variant="outline"
              type="button"
              onClick={() => props.onClose(false)}
            >
              Cancel
            </Button>
            <Button ref={submitRef} type="submit">
              Rename
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

const getUniqueTitle = (tasks: TaskPreview[], baseTitle: string) => {
  let copyNumber = 1;
  let newTitle = baseTitle;

  while (tasks.some((t) => t.title === newTitle)) {
    newTitle = `${baseTitle} (${copyNumber})`;
    copyNumber++;
  }

  return newTitle;
};
